import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Portal from './Portal';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  onCancel, 
  title = "Confirmar Ação", 
  message = "Tem certeza que deseja continuar?", 
  confirmText = "Confirmar", 
  cancelText = "Cancelar",
  showCancel = true,
  type = "warning" // warning, danger, info
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevBodyHeight = document.body.style.height;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      document.body.style.height = prevBodyHeight;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: 'text-danger-600',
          iconBg: 'bg-danger-100',
          confirmButton: 'bg-danger-600 hover:bg-danger-700 text-white',
          border: 'border-danger-200'
        };
      case 'info':
        return {
          icon: 'text-primary-600',
          iconBg: 'bg-primary-100',
          confirmButton: 'bg-primary-600 hover:bg-primary-700 text-white',
          border: 'border-primary-200'
        };
      default: // warning
        return {
          icon: 'text-warning-600',
          iconBg: 'bg-warning-100',
          confirmButton: 'bg-warning-600 hover:bg-warning-700 text-white',
          border: 'border-warning-200'
        };
    }
  };

  const styles = getTypeStyles();

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  return (
    <Portal>
      <div 
        className="modal-backdrop fixed inset-0 z-[99999] flex items-center justify-center overscroll-y-contain bg-black bg-opacity-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-card-light dark:bg-card-dark rounded-lg max-w-md w-full p-6 border border-border-light dark:border-border-dark">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
                aria-hidden
              >
                <AlertTriangle className={`h-5 w-5 ${styles.icon}`} />
              </div>
              <h3 className="truncate text-lg font-semibold text-foreground-light dark:text-foreground-dark">
                {title}
              </h3>
            </div>
            {showCancel && (
              <button
                type="button"
                onClick={handleCancel}
                className="flex-shrink-0 p-1 text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark transition-colors"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {message && (
            <div className="mb-6 text-sm text-muted-light dark:text-muted-dark whitespace-pre-line">
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
          )}

          <div className="flex w-full flex-row flex-nowrap items-stretch justify-center gap-2 sm:gap-3">
            {showCancel && (
              <button
                type="button"
                onClick={handleCancel}
                className="min-w-0 flex-1 basis-0 px-2 py-2 text-center text-xs font-medium text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark transition-colors sm:px-4 sm:text-sm"
              >
                {cancelText}
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              className={`min-w-0 flex-1 basis-0 rounded-lg px-2 py-2 text-center text-xs font-medium transition-colors sm:px-4 sm:text-sm ${styles.confirmButton}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default ConfirmationModal;
